const PRODUCT_FAMILY_STYLE_ORDER = [
  'T shirt',
  'Long Sleeve',
  'Mens Tank Top',
  'Dress',
  'Girlie Tee',
  'Racerback tank Top',
  'Crewneck Sweatshirt',
  'Pullover Sweatshirt',
  'Zipper Sweatshirt',
];

function normalizeStyleLabel(label) {
  return label
    .toLowerCase()
    .replace(/[''´`]/g, '')
    .replace(/[-–—]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getStyleSortIndex(label) {
  const normalized = normalizeStyleLabel(label);
  const index = PRODUCT_FAMILY_STYLE_ORDER.findIndex(
    (style) => normalizeStyleLabel(style) === normalized
  );
  return index === -1 ? PRODUCT_FAMILY_STYLE_ORDER.length : index;
}

function extractPillLabel(pillHtml) {
  const doc = new DOMParser().parseFromString(pillHtml, 'text/html');
  return doc.querySelector('.product-family-pill')?.textContent?.trim() ?? '';
}

function sortPillHtml(pillHtmlList) {
  return pillHtmlList.sort((a, b) => {
    const labelA = extractPillLabel(a);
    const labelB = extractPillLabel(b);
    const indexA = getStyleSortIndex(labelA);
    const indexB = getStyleSortIndex(labelB);

    if (indexA !== indexB) return indexA - indexB;
    return labelA.localeCompare(labelB);
  });
}

if (!customElements.get('product-family-picker')) {
  customElements.define(
    'product-family-picker',
    class ProductFamilyPicker extends HTMLElement {
      connectedCallback() {
        this.loadFamilyProducts();
      }

      buildSearchSectionUrl(query, familyId, page, extraParams) {
        const params = new URLSearchParams({
          q: query,
          type: 'product',
          section_id: 'product-family-results',
          family_id: familyId,
          page: String(page),
          current_product_id: this.dataset.currentProductId || '',
          label_metafield: this.dataset.labelMetafield || 'product.type',
          ...extraParams,
        });

        return `/search?${params.toString()}`;
      }

      normalizePath(href) {
        try {
          return new URL(href, window.location.origin).pathname;
        } catch {
          return href.split('?')[0];
        }
      }

      parseSectionPage(responseText) {
        const doc = new DOMParser().parseFromString(responseText, 'text/html');
        const results = doc.querySelector('.product-family-picker__results');
        const meta = doc.querySelector('[data-family-picker-meta]');
        const pills = results ? [...results.querySelectorAll('.product-family-pill')] : [];
        const totalPages = meta ? parseInt(meta.dataset.pages || '1', 10) : 1;

        return { pills, totalPages, hasResults: Boolean(results) };
      }

      markActivePill(target) {
        const currentPath = window.location.pathname;

        target.querySelectorAll('.product-family-pill').forEach((pill) => {
          if (this.normalizePath(pill.href) !== currentPath) return;

          pill.classList.add('product-family-pill--active');
          pill.setAttribute('aria-current', 'page');
        });
      }

      async fetchSearchSection(query, familyId, extraParams) {
        const pillsByPath = new Map();
        const firstResponse = await fetch(this.buildSearchSectionUrl(query, familyId, 1, extraParams));
        if (!firstResponse.ok) return pillsByPath;

        const firstPage = this.parseSectionPage(await firstResponse.text());
        if (!firstPage.hasResults) return pillsByPath;

        firstPage.pills.forEach((pill) => {
          pillsByPath.set(this.normalizePath(pill.href), pill.outerHTML);
        });

        const totalPages = Math.min(firstPage.totalPages, 10);

        for (let page = 2; page <= totalPages; page += 1) {
          const response = await fetch(this.buildSearchSectionUrl(query, familyId, page, extraParams));
          if (!response.ok) continue;
          const nextPage = this.parseSectionPage(await response.text());
          nextPage.pills.forEach((pill) => {
            pillsByPath.set(this.normalizePath(pill.href), pill.outerHTML);
          });
        }

        return pillsByPath;
      }

      async loadFamilyProducts() {
        const familyId = this.dataset.familyId;
        if (!familyId) return;

        const target = this.querySelector('[data-family-picker-results]');
        if (!target) return;

        const extraParams = {};
        if (this.dataset.hideUnavailable === 'true') {
          extraParams.hide_unavailable = 'true';
        }

        try {
          let pillsByPath = await this.fetchSearchSection(`tag:${familyId}`, familyId, extraParams);

          if (pillsByPath.size <= 1) {
            pillsByPath = await this.fetchSearchSection(familyId, familyId, extraParams);
          }

          if (pillsByPath.size <= 1) return;

          target.innerHTML = sortPillHtml(Array.from(pillsByPath.values())).join('');
          this.markActivePill(target);
          this.hidden = false;
        } catch (error) {
          console.error('Product family picker failed to load', error);
        }
      }
    }
  );
}
